import {
  directUploadAll,
  FileDropzone,
  Upload as UploadResult,
  withScrubbedName,
} from '@execonline-inc/file-upload.private';
import { identity } from '@kofno/piper';
import { observer } from 'mobx-react';
import { fromArray } from 'nonempty-list';
import * as React from 'react';
import AttachmentUploadStore from '../../../AttachmentUploadStore';
import WithCurrentUser from '../../../CurrentUser/Context/WithCurrentUser';
import { CurrentUserResource } from '../../../CurrentUser/Types';
import { T, translation, TranslationsContext } from '../../../Translations';
import { acceptedFileExtensions } from '../../../Uploads/AcceptedExtensions';
import {
  attachmentErrorMessage,
  attachmentRules,
  DirectUploadFailure,
  directUploadConfig,
} from '../../../Uploads/DirectUploadConfig';
import { Upload } from '../../../Uploads';
import * as style from './style.module.css';

type AttachmentPurpose = 'segment_attachment' | 'action_plan_attachment';

interface Props {
  store: AttachmentUploadStore;
  // Optional narrower allowlist. When omitted (e.g. team-discussion
  // attachments), uploads fall back to the broad default extension list.
  acceptExtensions?: string[];
  // The DirectUpload::Purpose the server applies. It must match the extension
  // list: action-plan submissions go to the AI feedback Lambda and take a
  // narrower set than team-discussion attachments.
  purpose?: AttachmentPurpose;
  // data-intercom-target value for Intercom product tours and analytics.
  // Lands on the pick-file button, which is the click target. The Filestack
  // uploader had to put it on the wrapper instead, because the button itself
  // belonged to a third-party component.
  intercomTarget?: string;
}

/**
 * Attachment picker for segment submissions. Drag files onto the area or use
 * the button, then the browser uploads them straight to S3 and the store
 * keeps the resulting signed ids for the submission to send.
 */
const AttachmentUploader: React.FC<Props> = ({
  store,
  acceptExtensions,
  purpose = 'segment_attachment',
  intercomTarget,
}) => (
  <WithCurrentUser
    children={(currentUserResource) => (
      <TranslationsContext.Consumer>
        {(ts) => (
          <FileDropzone
            rules={attachmentRules(acceptExtensions ?? acceptedFileExtensions)}
            className={style.dropzone}
            activeClassName={style.dropzoneActive}
            inputLabel={translation('Pick file')(ts)}
            onAccepted={onAccepted(store, currentUserResource, purpose)}
            onRejected={(error) => store.error(attachmentErrorMessage(error))}
          >
            {(open) => (
              <div data-test-attachment-uploader={true}>
                <span className={style.dropHint}>
                  <T kind="Drag and drop, or" />
                </span>
                <button
                  type="button"
                  className={style.pickFileButton}
                  onClick={open}
                  data-intercom-target={intercomTarget}
                >
                  <T kind="Pick file" />
                </button>
              </div>
            )}
          </FileDropzone>
        )}
      </TranslationsContext.Consumer>
    )}
  />
);

// Every accepted file is a blob upload, because both attachment purposes run
// in blob mode. Drop anything else rather than submit a file the server
// cannot attach.
const toUploads = (results: ReadonlyArray<UploadResult>): ReadonlyArray<Upload> =>
  results.reduce<Upload[]>(
    (soFar, result) =>
      result.kind === 'blob-upload'
        ? [...soFar, { signedId: result.signedId, filename: result.filename, previewUrl: '' }]
        : soFar,
    [],
  );

const onAccepted =
  (
    store: AttachmentUploadStore,
    currentUserResource: CurrentUserResource,
    purpose: AttachmentPurpose,
  ) =>
  (files: ReadonlyArray<File>): void => {
    store.loading();

    directUploadConfig(currentUserResource, purpose)
      .andThen((config) =>
        directUploadAll(files.map(withScrubbedName), config).mapError<DirectUploadFailure>(
          identity,
        ),
      )
      .fork(
        (error) => store.error(attachmentErrorMessage(error)),
        (results) => {
          fromArray(toUploads(results)).do(store.uploadSuccessful);
        },
      );
  };

export default observer(AttachmentUploader);
